/* eslint-disable max-lines */
import React from 'react'

import { FaqCategory } from '../types'

/**
 * FAQ sections data
 * Contains all FAQ categories and items
 */

export const doingBusinessSection: FaqCategory = {
  id: 'doing-business',
  title: 'Doing Business With Woot',
  items: [
    {
      id: 'why-woot',
      question: 'Why Woot?',
      answer:
        "Well, we're really good at what we do (if we do say so ourselves). Our partnership gives you the opportunity to sell your products via Woot.com, through Woot as a 3P seller on Amazon.com, and through our dedicated wholesale channel. We have multiple fulfillment methods and we can sell at a high volume really quickly (sometimes within 24 hours or less!)",
    },
    {
      id: 'what-makes-woot-different',
      question: 'What makes Woot different from the other deal sites?',
      answer:
        "Woot is the original Daily Deal site, and since 2010 we are able to fulfill customer orders using Amazon's distribution network. Our knowledgeable and active community of users offer and share detailed insights on products. The sales and community comments also give vendors valuable insight into which features drive sales. Plus, we showcase products with wacky write-ups and weird videos on our site and on social media, which keeps our fans searching for more.",
    },
    {
      id: 'amazon-opportunities',
      question: 'What Amazon opportunities could you get as a Woot Vendor?',
      answer:
        'Woot could give you access to key deal events such as their Deal of the Day or Lightning Deals programs and seasonal events like Prime Days, Black Friday, and Cyber Monday. Plus, there are potential opportunities for you to have featured segments on Amazon live broadcasts. We can even set up A+ content for you!',
    },
    {
      id: 'wholesale-channel',
      question: "What is Woot's wholesale channel?",
      answer:
        'It is the ideal channel for end-of-life, liquidation, and residual inventory. Woot has access to liquidators located in the US, Canada, UK, and beyond.',
    },
  ],
}

export const vendorOnboardingSection: FaqCategory = {
  id: 'vendor-onboarding',
  title: 'Vendor Onboarding',
  items: [
    {
      id: 'become-vendor',
      question: 'How do I become a Woot vendor?',
      answer: (
        <span>
          First, log in{' '}
          <a
            href="https://vendor.woot.com/"
            target="_blank"
            rel="noopener noreferrer"
          >
            here
          </a>
          {
            " and submit your new vendor application. The friendly Woot team will follow up within the next 10 business days. If we decide to move forward, you will be asked to complete a tax interview. That's it! Easy, right? Right."
          }
        </span>
      ),
    },
  ],
}

export const launchingDealSection: FaqCategory = {
  id: 'launching-deal',
  title: 'Launching a Deal as a Woot Vendor',
  items: [
    {
      id: 'selection-interested',
      question: 'What selection is Woot interested in Buying?',
      answer: (
        <span>
          Vendors should share their best deal offers, and our vendor management
          team will offer feedback. Keep the following criteria in mind when
          submitting a deal:
          <ul>
            <li>
              Does this deal present a strong discount compared to the lowest
              price online?
            </li>
            <li>
              Does the product have a 3.5+ star rating on{' '}
              <a href="https://amazon.com/" target="_blank">
                Amazon.com
              </a>{' '}
              or a similar website?
            </li>
            <li>
              Does the product have a high volume of reviews on{' '}
              <a href="https://amazon.com/" target="_blank">
                Amazon.com
              </a>{' '}
              or a similar website?
            </li>
            <li>Is it a recognizable brand?</li>
            <li>Is the product trendy or in-demand?</li>
          </ul>
        </span>
      ),
    },
    {
      id: 'discount-pricing',
      question:
        'What kind of discount off retail does Woot price its Deals for customers?',
      answer:
        "We look to offer customers great deals and generally target at least 20% off the currently lowest online retail price. The discounts off retail prices offered in Woot's deals are based on a number of different factors.",
    },
    {
      id: 'submit-deal',
      question: 'How do I submit a deal?',
      answer:
        "Once you are onboarded, our vendor management team will reach out and send you a template which you'll use to send us your deals. Hooray!",
    },
    {
      id: 'launch-deal',
      question: 'How quickly can I launch a deal as a Woot Vendor?',
      answer: 'Within 1-3 weeks of approving your deal.',
    },
    {
      id: 'purchase-order',
      question: 'How do I receive a Purchase Order from Woot?',
      answer:
        "We'll agree to product quantities, cost, and available dates and cut a PO to reserve the product. As a Woot vendor you will need to reserve the inventory quantity once we send the PO.",
    },
    {
      id: 'inventory',
      question: 'Does Woot buy and hold inventory?',
      answer:
        'We do buy inventory to fulfill using our distribution network. Vendors may also fulfill customer shipments on our behalf. "Drop-ship," as the kids like to say.',
    },
    {
      id: 'freight',
      question: 'Who pays the freight for inbound inventory?',
      answer: "We'll negotiate this during the deal evaluation.",
    },
    {
      id: 'product-specs',
      question: 'How do I provide product specifications and images to Woot?',
      answer:
        "Your dedicated vendor management team will send you an event setup template to gather specification and images. We'll use assets to build the product detail page.",
    },
    {
      id: 'product-doesnt-sell',
      question: "If the product doesn't sell, what does Woot do with it?",
      answer:
        "It's cool, we know a guy who knows guy… in other words, we will consider other deal formats to sell through the inventory. Like we said, we're good at this stuff.",
    },
    {
      id: 'edi',
      question: 'Does Woot offer EDI?',
      answer: 'Not currently, but we have plans to offer EDI in the future.',
    },
  ],
}

export const customerOrdersSection: FaqCategory = {
  id: 'customer-orders',
  title: 'Customer Orders and Fulfillment',
  items: [
    {
      id: 'fulfillment-channels',
      question: 'What are the different fulfillment channels?',
      answer: (
        <span>
          Here are the different fulfillment channels:
          <ul>
            <li>
              Inventory (Woot pays to bring into Amazon Fulfillment Centers)
            </li>
            <li>
              Inventory (Vendor pays to bring into Amazon Fulfillment Centers)
            </li>
            <li>
              Inventory Transfer (Vendor has inventory in Amazon Fulfillment
              Centers and transfers inventory to Woot)
            </li>
            <li>Dropship (Woot Pays Freight)</li>
            <li>Dropship (Vendor Pays Freight)</li>
          </ul>
        </span>
      ),
    },
    {
      id: 'dropship-process',
      question: 'How does the Drop-Ship process work with Woot?',
      answer: (
        <span>
          We send encrypted order files during the event, and the vendor returns
          tracking numbers for each order to confirm shipment. For additional
          information, check out our{' '}
          <a href="/DropshipProgramGuide.pdf" target="_blank">
            Dropship Program Guide
          </a>
          .
        </span>
      ),
    },
    {
      id: 'inventory-mixed',
      question:
        'Is Woot inventory mixed with Amazon inventory if I use one of the Amazon fulfillment channels?',
      answer: 'No, Woot owns the inventory we fulfill directly.',
    },
  ],
}

export const vendorPaymentSection: FaqCategory = {
  id: 'vendor-payment',
  title: 'Vendor Payment',
  items: [
    {
      id: 'payment-terms',
      question: 'What vendor payment terms does Woot offer?',
      answer: "We'll set terms during vendor setup. Standard terms are Net60.",
    },
    {
      id: 'invoice',
      question: 'How does Woot invoice?',
      answer: (
        <span>
          Woot requires a consolidated invoice for each Purchase Order (PO) in
          order to receive payment.
          <br />
          <br />
          A consolidated document should include all items shipped under a
          specific Woot PO Number and have one unique invoice. Each invoice
          should have the following:
          <br />
          <br />
          <b>The Invoice Date:</b> So we know when to pay the invoice based on
          our terms with your company. This should be the date the invoice is
          created. Accounting will use the invoice date to enter, unless it is
          received after the invoice date, in which accounting will use the date
          the invoice is received.
          <br />
          <b>PO Number:</b> Some vendors have several POs going at a time.
          Referencing the PO number allows us to match the invoice to the PO
          without delays.
          <br />
          <b>Your Invoice Number:</b> So your AR department will know how to
          apply the payment once received.
          <br />
          <b>Consolidated List of Items Sold:</b> Not customer numbers, invoice
          numbers, or order numbers. We need to know exactly what product we are
          paying for.
          <br />
        </span>
      ),
    },
    {
      id: 'flexible-payment',
      question: 'Can I have flexible payment terms?',
      answer:
        'Yes! We offer shorter terms for discounted payment remittance. For example, 2%30Net60. If Woot pays in 30 days, we take a 2% discount off of your payment remittance. Once again, we will set terms during vendor setup.',
    },
    {
      id: 'cash-advance',
      question: 'Does Woot pay Cash In Advance?',
      answer: "Listen, we'll set terms during vendor setup, okay?",
    },
    {
      id: 'accounts-payable',
      question:
        'How do I contact Accounts Payable? Who should I speak with for my account?',
      answer:
        "Boy, you've got a lot of questions, don't you? During vendor setup, we'll provide contact information for Accounts Payable, and you can always reach out to your vendor management team with questions.",
    },
  ],
}

export const returnsCustomerServiceSection: FaqCategory = {
  id: 'returns-customer-service',
  title: 'Returns and Customer Service',
  items: [
    {
      id: 'customer-service',
      question: 'How does Woot handle customer service?',
      answer:
        'Customers contact Woot Customer Service through their Woot Account for any questions or issues. We provide e-mail support and strive to respond to all customer e-mails within 1 business day.',
    },
    {
      id: 'returns-policy',
      question:
        'Does Woot accept returns from customers? What is the return policy?',
      answer:
        'All products are covered by a 90-day Woot Warranty at a minimum, and we review returns on a case-by-case basis.',
    },
    {
      id: 'vendor-returns',
      question: 'What return terms does Woot expect from Vendors?',
      answer:
        "We'll set terms when evaluating the deal. Please include the standard manufacturer warranty information, at a minimum, when sharing offer details.",
    },
  ],
}

export const amazonRelationshipSection: FaqCategory = {
  id: 'amazon-relationship',
  title: 'How Woot Works With Amazon',
  items: [
    {
      id: 'amazon-affiliation',
      question: 'How is Woot affiliated with Amazon?',
      answer:
        "Woot is a wholly owned subsidiary of Amazon. No, we don't play golf with Jeff Bezos. At least not since we beat him that one time.",
    },
    {
      id: 'existing-relationship',
      question:
        "Does doing business with Woot affect a Vendor's existing relationship with Amazon?",
      answer:
        'No, Woot and Amazon have distinct vendor management teams, each planning their categories independently.',
    },
    {
      id: 'sell-on-amazon',
      question: 'Does Woot sell on Amazon?',
      answer: (
        <span>
          Yes, we are able to sell as a third-party seller. It's pretty cool.
          <br />
          Ask your Vendor Manager how you could sell on Amazon through Woot.
        </span>
      ),
    },
    {
      id: 'amazon-deals',
      question:
        'Do Woot Vendors have opportunities to get a spot on Amazon best and top deals?',
      answer:
        'Yep. Your vendor management team will consider Amazon Deals as a possible promotion channel when reviewing your deal offer.',
    },
  ],
}
/* eslint-enable max-lines */
